import { useState, useEffect } from "react";
import { PartyPopper } from "lucide-react";

import { api, getAccessToken } from "../lib/api.js";
import { DashboardGrid, DashboardEditToggle } from "../components/dashboard/DashboardGrid.js";
import { DASHBOARD_WIDGETS } from "../components/dashboard/widgets/index.js";
import { useMe } from "../context/MeContext.js";
import { greetingLine } from "../lib/greeting.js";
import ShiftNotSetBanner from "../components/ShiftNotSetBanner.js";
import { FestiveLine } from "../components/ui/festive-line.js";
import { useActiveFestival } from "../hooks/useActiveFestival.js";

function decodeToken(token: string): { role: string; email: string; employeeId: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), email: String(p["email"] ?? ""), employeeId: String(p["employeeId"] ?? "") };
  } catch { return { role: "", email: "", employeeId: "" }; }
}

/**
 * The page is deliberately thin: it owns the heading and the one-time onboarding
 * congratulation, and hands everything else to DashboardGrid. Widgets, their
 * data and their arrangement are not its concern — see components/dashboard/.
 */
export default function DashboardPage() {
  const { role, employeeId } = decodeToken(getAccessToken() ?? "");
  const me = useMe();
  const [justCompleted, setJustCompleted] = useState(false);
  // Owned here, not in the grid, because the toggle sits in this header.
  const [editing, setEditing] = useState(false);
  const festive = useActiveFestival();

  // Shown once, then marked seen server-side — a transient acknowledgement rather
  // than a widget, so it is not something the employee arranges or hides.
  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    api.get<{ status: string; completionBannerSeenAt: string | null }>(`/employees/${employeeId}/onboarding`)
      .then((res) => {
        if (cancelled) return;
        if (res.data.status === "COMPLETED" && !res.data.completionBannerSeenAt) {
          setJustCompleted(true);
          api.post(`/employees/${employeeId}/onboarding/completion-banner-seen`).catch(() => {});
        }
      })
      .catch(() => { /* the banner simply doesn't show */ });
    return () => { cancelled = true; };
  }, [employeeId]);

  return (
    // Fills the width the shell gives it, like every other page. The fixed cap this
    // replaced left a growing dead strip down the right of wide screens.
    <div className="w-full p-3 sm:p-4">
      {/* Title and greeting share one baseline rather than stacking, which gives
          the tiles below a line of vertical space back. They wrap on narrow screens,
          and the Customise toggle stays pinned to the right. */}
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1.5 sm:mb-5 sm:gap-y-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:gap-x-3 sm:gap-y-1">
          <h1 className="text-[17px] font-bold sm:text-[22px]">Dashboard</h1>
          <p className="text-[11px] text-surface-500 sm:text-[13px]">{greetingLine(me.firstName)}</p>
        </div>
        <DashboardEditToggle editing={editing} onToggle={() => setEditing((v) => !v)} />
        {/* Last in the DOM so it wraps onto its own row under the title, after the toggle. */}
        {festive && <FestiveLine active={festive} />}
      </div>

      {/* Above the widgets: an employee with no shift is being measured against a
          default, and that is worth knowing before anything else on the page. It
          renders nothing once a shift exists. */}
      <div className="mb-3 empty:mb-0 sm:mb-5">
        <ShiftNotSetBanner />
      </div>

      {justCompleted && (
        <div className="mb-5 rounded-lg border border-[var(--color-success-border)] bg-[var(--color-success-bg-subtle)] p-5 text-[var(--color-success-fg)]">
          <h2 className="mb-1 flex items-center gap-1.5 text-base font-bold">
            <PartyPopper size={16} /> Onboarding complete!
          </h2>
          <p className="text-[13px]">Your profile is all set up. Welcome aboard!</p>
        </div>
      )}

      <DashboardGrid registry={DASHBOARD_WIDGETS} ctx={{ employeeId, role }} editing={editing} />
    </div>
  );
}
